import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { OptimizedImage } from "@/components/ui/optimized-image"
import { VideoPhone } from "@/components/public/video-phone"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import { RelatedPages } from "@/components/public/related-pages"
import { relatedFor } from "@/data/related-pages"
import { cdnUrl } from "@/lib/cdn"
import { imageSrcSet } from "@/data/location-images"
import type { SeoMeta } from "@/types"

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

type ComparisonRow = {
  label: string
  canva: string
  quiteright: string
  highlight?: boolean
}

const COMPARISON: ComparisonRow[] = [
  {
    label: "Built for property",
    canva: "Generic templates",
    quiteright: "Purpose-built for listings",
    highlight: true,
  },
  {
    label: "Listing import",
    canva: "Manual: copy photos, text, price",
    quiteright: "Paste a URL, done in seconds",
    highlight: true,
  },
  {
    label: "Voiceover",
    canva: "Not included",
    quiteright: "AI voiceover, multiple voices",
    highlight: true,
  },
  {
    label: "Property scripts",
    canva: "Write your own",
    quiteright: "Auto-generated from listing details",
  },
  {
    label: "Video templates",
    canva: "Generic, needs heavy editing",
    quiteright: "Prestige, Modern, Classic, Bold",
  },
  {
    label: "Time per video",
    canva: "30\u201360 minutes",
    quiteright: "Under 60 seconds",
    highlight: true,
  },
  {
    label: "Music",
    canva: "Limited free library",
    quiteright: "Included, royalty-free",
  },
  {
    label: "Social formats",
    canva: "Manual resizing per platform",
    quiteright: "Reels, TikTok, Shorts, landscape",
  },
  {
    label: "Agency branding",
    canva: "Manual placement each time",
    quiteright: "Automatic on every video",
  },
  {
    label: "Learning curve",
    canva: "Moderate: drag-and-drop editor",
    quiteright: "None: paste URL, pick template",
  },
  {
    label: "Consistency at scale",
    canva: "Hard to maintain across team",
    quiteright: "Every video, same brand standard",
    highlight: true,
  },
]

const SPEED_STATS = [
  {
    value: "60s",
    label: "listing to video",
    detail: "Paste a URL, pick a template, and your video is ready to publish.",
  },
  {
    value: "0",
    label: "design skills needed",
    detail: "No dragging, no dropping, no resizing. Just property marketing that works.",
  },
  {
    value: "50",
    label: "videos per month",
    detail: "Every listing gets a video. Not just the ones you have time for.",
  },
  {
    value: "10+",
    label: "hours saved monthly",
    detail: "No fiddling with templates. That\u2019s time back for winning instructions.",
  },
]

type FaqItem = { q: string; a: string }

const FAQ: FaqItem[] = [
  {
    q: "Can't I just use Canva for property videos?",
    a: "You can, but you'll spend 30\u201360 minutes per video manually importing photos, writing scripts, choosing music, and resizing for each platform. Canva is a general design tool. QuiteRight is built specifically for property marketing, so it does in 60 seconds what takes an hour in Canva.",
  },
  {
    q: "Is Canva free and QuiteRight isn't?",
    a: "Canva's free tier is limited: no premium templates, restricted music, and watermarks on some content. Canva Pro costs \u00A3100/year per user. QuiteRight's Agent plan is \u00A349/month and includes everything: voiceover, music, branding, and unlimited social formats. The real cost is your time, and QuiteRight saves over 10 hours per month.",
  },
  {
    q: "What about Canva's video templates?",
    a: "Canva has thousands of video templates, but none are built for property listings. You'll need to manually replace every photo, rewrite every text block, adjust timings, and add your own voiceover. QuiteRight templates are designed for property: they know where the price goes, how to sequence room photos, and what script to generate.",
  },
  {
    q: "Can my whole team use QuiteRight consistently?",
    a: "Yes. Every team member gets the same templates, the same brand settings, and the same quality output. With Canva, each negotiator creates videos differently, leading to inconsistent branding. QuiteRight enforces your brand standard automatically.",
  },
  {
    q: "What if I already have a Canva subscription?",
    a: "Keep it for brochures, social graphics, and general marketing. Use QuiteRight for property videos specifically. They solve different problems. Canva is a Swiss army knife; QuiteRight is a purpose-built tool for the one thing that drives viewings: property video ads.",
  },
]

/* ------------------------------------------------------------------ */
/*  Components                                                         */
/* ------------------------------------------------------------------ */

function ComparisonTable({ rows }: { rows: ComparisonRow[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[540px] text-left">
        <thead>
          <tr className="border-b-2 border-stone">
            <th className="pb-3 pr-4 text-xs font-medium uppercase tracking-[0.15em] text-slate">
              Feature
            </th>
            <th className="pb-3 px-4 text-center text-xs font-medium uppercase tracking-[0.15em] text-slate">
              Canva
            </th>
            <th className="pb-3 pl-4 text-center text-xs font-medium uppercase tracking-[0.15em] text-red">
              QuiteRight
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className={`border-b border-stone/50 ${row.highlight ? "bg-cream/50" : ""}`}
            >
              <td className="py-3.5 pr-4 text-sm font-medium text-charcoal">
                {row.label}
              </td>
              <td className="py-3.5 px-4 text-center text-sm text-slate">
                {row.canva}
              </td>
              <td className="py-3.5 pl-4 text-center text-sm font-semibold text-charcoal">
                {row.quiteright}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function FaqAccordion({ items }: { items: FaqItem[] }) {
  return (
    <dl className="divide-y divide-stone">
      {items.map((item, i) => (
        <details key={i} className="group">
          <summary className="flex cursor-pointer items-start justify-between gap-4 py-5 text-left [&::-webkit-details-marker]:hidden">
            <dt className="text-sm font-semibold leading-relaxed text-charcoal">
              {item.q}
            </dt>
            <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-stone text-slate transition-transform duration-200 group-open:rotate-45">
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                className="size-3"
              >
                <path d="M8 3v10M3 8h10" />
              </svg>
            </span>
          </summary>
          <dd className="pb-5 text-sm leading-relaxed text-slate">{item.a}</dd>
        </details>
      ))}
    </dl>
  )
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function QuiterightVsCanva({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={meta.image} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: "QuiteRight vs Canva for Property Videos",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-02-25",
            dateModified: "2026-02-25",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>

        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "QuiteRight vs Canva" }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-6xl">
          <div className="grid items-center gap-12 md:grid-cols-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
                Compare
              </p>
              <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl">
                Canva wasn&rsquo;t built for property
                <span className="text-red">.</span>
              </h1>
              <p className="mt-6 max-w-lg text-lg text-slate">
                Canva is a brilliant design tool. But making property videos in
                Canva means starting from scratch every time: importing photos,
                writing scripts, choosing music, resizing for each platform.
                QuiteRight does all of that in 60 seconds.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Link
                  href="/sign-up"
                  className="inline-flex items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90"
                >
                  Start your free trial
                </Link>
                <Link
                  href="/demo"
                  className="inline-flex items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone"
                >
                  Book a demo
                </Link>
              </div>
            </div>
            <div className="relative flex justify-center">
              <VideoPhone />
              <div className="absolute -bottom-4 -left-4 rounded-lg bg-charcoal px-4 py-2.5 shadow-md md:-left-2">
                <p className="text-xs font-medium text-warm-grey">Created in</p>
                <p className="font-display text-xl font-bold text-white">60 seconds</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Speed stats */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Purpose-built
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Property videos need a property tool
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-base text-slate">
              Canva makes you a designer. QuiteRight makes you an agent with a
              full marketing department. There&rsquo;s a difference.
            </p>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {SPEED_STATS.map((stat, i) => (
              <ScrollReveal key={i}>
                <div className="rounded-xl border border-stone bg-white p-6 text-center">
                  <p className="font-display text-4xl font-bold text-charcoal">
                    {stat.value}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-red">
                    {stat.label}
                  </p>
                  <p className="mt-3 text-xs leading-relaxed text-slate">
                    {stat.detail}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Workflow comparison */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              The workflow
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              One listing, two approaches
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            <ScrollReveal>
              <div className="flex h-full flex-col rounded-xl border border-stone bg-white p-6 md:p-8">
                <h3 className="font-display text-lg font-bold text-charcoal">
                  With Canva
                  <span className="text-red">:</span>
                </h3>
                <ol className="mt-6 flex-1 space-y-4 text-sm leading-relaxed text-slate">
                  {[
                    "Download photos from the portal",
                    "Open Canva, find a video template",
                    "Replace every placeholder image",
                    "Rewrite all text: price, address, features",
                    "Choose background music",
                    "Adjust timings for each slide",
                    "Export for Instagram",
                    "Re-export at different size for TikTok",
                    "Re-export again for YouTube",
                  ].map((step, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-stone text-xs font-semibold text-slate">
                        {i + 1}
                      </span>
                      <span className="pt-0.5">{step}</span>
                    </li>
                  ))}
                </ol>
                <div className="mt-6 rounded-lg bg-stone/50 px-4 py-3 text-center">
                  <p className="text-xs font-medium uppercase tracking-[0.15em] text-slate">Total time</p>
                  <p className="mt-1 font-display text-xl font-bold text-charcoal">30\u201360 minutes</p>
                </div>
              </div>
            </ScrollReveal>
            <ScrollReveal>
              <div className="flex h-full flex-col rounded-xl border-2 border-red/20 bg-red/5 p-6 md:p-8">
                <h3 className="font-display text-lg font-bold text-charcoal">
                  With QuiteRight
                  <span className="text-red">:</span>
                </h3>
                <ol className="mt-6 flex-1 space-y-4 text-sm leading-relaxed text-slate">
                  {[
                    "Paste the listing URL",
                    "Pick a template",
                    "Publish to all platforms",
                  ].map((step, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-red text-xs font-semibold text-white">
                        {i + 1}
                      </span>
                      <span className="pt-0.5">{step}</span>
                    </li>
                  ))}
                </ol>
                <div className="mt-6 overflow-hidden rounded-lg">
                  <OptimizedImage
                    src={cdnUrl("/property-canynge-square-800w.webp")}
                    srcSet={imageSrcSet("/property-canynge-square-800w.webp")}
                    sizes="(max-width: 768px) 100vw, 400px"
                    alt="Property video created with QuiteRight for Canynge Square, Clifton"
                    className="w-full rounded-lg"
                    width={800}
                    height={600}
                  />
                </div>
                <div className="mt-4 rounded-lg bg-red/10 px-4 py-3 text-center">
                  <p className="text-xs font-medium uppercase tracking-[0.15em] text-red">Total time</p>
                  <p className="mt-1 font-display text-xl font-bold text-charcoal">60 seconds</p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Full comparison table */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Side by side
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              The full comparison
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <ComparisonTable rows={COMPARISON} />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              FAQ
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <FaqAccordion items={FAQ} />
            </ScrollReveal>
          </div>
        </div>
      </section>

      <RelatedPages links={relatedFor("quiteright-vs-canva")} tone="white" />

      {/* CTA */}
      <section className="border-t border-stone bg-cream px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <ScrollReveal>
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Stop designing. Start marketing
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-slate">
              Your listings deserve purpose-built video marketing, not a generic
              design tool. Try QuiteRight free for 14 days.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href="/sign-up"
                className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90 sm:w-auto"
              >
                Start your free trial
              </Link>
              <Link
                href="/demo"
                className="inline-flex w-full items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone sm:w-auto"
              >
                Book a demo
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </PublicLayout>
  )
}
